import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate, Link } from 'react-router-dom';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import GoogleGIcon from '../components/GoogleGIcon';
import {
  clearPendingGoogleSignup,
  clearSignupToken,
  mapGoogleAuthError,
  signInWithGoogle,
} from '../lib/googleAuth';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [capClosed, setCapClosed] = useState(false);
  const [inviteOnly, setInviteOnly] = useState(false);
  const [mode, setMode] = useState<'login' | 'forgot'>('login');

  useEffect(() => {
    async function loadAvailability() {
      const { data, error: rpcError } = await supabase.rpc('get_signup_availability');
      if (rpcError || !data || typeof data !== 'object' || Array.isArray(data)) return;
      if ((data as { remaining?: unknown }).remaining === 0) {
        setCapClosed(true);
      }
      if ((data as { require_token?: unknown }).require_token === true) {
        setInviteOnly(true);
      }
    }
    loadAvailability();
  }, []);

  const switchMode = (next: 'login' | 'forgot') => {
    setMode(next);
    setError(null);
    setSuccess(null);
    setLoading(false);
    setGoogleLoading(false);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      navigate('/');
    }
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    setError(null);
    setSuccess(null);
    clearPendingGoogleSignup();
    clearSignupToken();
    const { error } = await signInWithGoogle();
    if (error) {
      setError(mapGoogleAuthError(error.message).message);
      setGoogleLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    const redirectTo = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo,
    });

    setLoading(false);

    if (error) {
      setError(error.message);
      return;
    }

    setSuccess(
      'Se existir uma conta com este e-mail, enviamos um link para redefinir a senha. Confira sua caixa de entrada.'
    );
  };

  const busy = loading || googleLoading;

  return (
    <div className="glass-card">
      <div className="auth-brand">
        <img src="/assets/icon-mark.png" alt="Seu.video" className="auth-brand-mark" />
      </div>
      <h1>{mode === 'login' ? 'Bem-vindo ao Seu.Video' : 'Redefinir senha'}</h1>
      <p className="subtitle">
        {mode === 'login'
          ? 'Faça login para acessar suas propriedades virtuais.'
          : 'Informe seu e-mail e enviaremos um link para criar uma nova senha.'}
      </p>

      {error && (
        <div className="error-message" role="alert">
          <AlertCircle size={18} aria-hidden="true" />
          {error}
        </div>
      )}

      {success && (
        <div className="success-message" role="status" aria-live="polite">
          <CheckCircle2 size={18} aria-hidden="true" />
          {success}
        </div>
      )}

      {mode === 'login' ? (
        <>
          <button
            type="button"
            className="btn-google"
            onClick={handleGoogle}
            disabled={busy}
          >
            <GoogleGIcon />
            {googleLoading ? 'Redirecionando...' : 'Continuar com Google'}
          </button>

          <div className="auth-divider" role="separator">
            <span>ou</span>
          </div>

          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="login-email">E-mail</label>
              <input
                id="login-email"
                type="email"
                required
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="joao@example.com"
              />
            </div>

            <div className="form-group">
              <div className="form-label-row">
                <label htmlFor="login-password">Senha</label>
                <button
                  type="button"
                  className="auth-inline-link"
                  onClick={() => switchMode('forgot')}
                >
                  Esqueceu a senha?
                </button>
              </div>
              <input
                id="login-password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>

            <button type="submit" className="btn-primary" disabled={busy}>
              {loading ? 'Entrando…' : 'Entrar na minha conta'}
            </button>
          </form>
        </>
      ) : (
        <form onSubmit={handleForgotPassword}>
          <div className="form-group">
            <label htmlFor="forgot-email">E-mail da conta</label>
            <input
              id="forgot-email"
              type="email"
              required
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="joao@example.com"
              autoFocus
            />
          </div>

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Enviando…' : 'Enviar link de redefinição'}
          </button>
        </form>
      )}

      <div className="auth-link">
        {mode === 'forgot' ? (
          <button type="button" className="auth-inline-link" onClick={() => switchMode('login')}>
            Voltar ao login
          </button>
        ) : capClosed ? (
          <>Cadastro de novas organizações encerrado. <Link to="/register">Saiba mais</Link></>
        ) : inviteOnly ? (
          <>Novas organizações entram por um link de cadastro.</>
        ) : (
          <>Novo por aqui? <Link to="/register">Crie sua organização</Link></>
        )}
      </div>
    </div>
  );
}
